import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Receipt, Wallet, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import useCobroStore from '../../../store/cobro';
import { Button } from '../../../components/ui/Button';
import { CopyButton } from '../../../components/ui/CopyButton';
import { PageHeader } from '../../../components/ui/PageHeader';
import { EmptyState } from '../../../components/ui/EmptyState';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { useIsMobile } from '../../../hooks/useIsMobile';
import PaymentStatusBadge from '../../adminView/cobro/components/PaymentStatusBadge';
import { DueDateHint } from '../../adminView/cobro/components/DueDateInfo';
import {
  formatCurrency,
  formatDate,
  CHARGE_MODE_LABELS,
  dueDateStatus,
  isDebtNote,
} from '../../adminView/cobro/utils/cobroHelpers';
import { amountDueOf, paidAmountOf } from '../../adminView/cobro/utils/payments';

// Notas por pagar:  Folio · Periodo · Modo · Total · Pagado · Saldo · Estado · Acciones
const UNPAID_COLS = '0.7fr 1.3fr 0.8fr 0.9fr 0.9fr 0.9fr 1fr auto';
// Historial:        Folio · Periodo · Total · Pagado · Saldo · Estado · Pagada · Acciones
const HISTORY_COLS = '0.7fr 1.2fr 0.9fr 0.9fr 0.9fr 1fr 0.9fr auto';

const PENDING_STATES = ['unpaid', 'partially_paid', 'overdue'];

const PagosList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    myNotes,
    loadingMine,
    fetchMyNotes,
    myDebtNotes,
    myDebtCount,
    loadingMyDebt,
    myDebtError,
    fetchMyDebtNotes,
  } = useCobroStore((s) => ({
    myNotes: s.myNotes,
    loadingMine: s.loadingMine,
    fetchMyNotes: s.fetchMyNotes,
    myDebtNotes: s.myDebtNotes,
    myDebtCount: s.myDebtCount,
    loadingMyDebt: s.loadingMyDebt,
    myDebtError: s.myDebtError,
    fetchMyDebtNotes: s.fetchMyDebtNotes,
  }));

  // Las impagas van en una consulta aparte (`debt=true`): el listado general trae solo la
  // primera página por fecha de creación, y una deuda vieja quedaba afuera de "por pagar".
  useEffect(() => {
    fetchMyNotes();
    fetchMyDebtNotes();
  }, [fetchMyNotes, fetchMyDebtNotes]);

  // El server ya filtra por deuda; se re-filtra para que un billing sin `debt` (despliegue
  // a medias) no mezcle notas pagadas entre las "por pagar".
  const unpaid = useMemo(() => myDebtNotes.filter(isDebtNote), [myDebtNotes]);
  const history = useMemo(
    () => myNotes.filter((n) => n.paymentStatus === 'paid' || n.paymentStatus === 'cancelled'),
    [myNotes],
  );
  const loading = loadingMine || loadingMyDebt;

  const strip = useMemo(() => {
    // "Por pagar" es la suma de SALDOS (una nota parcial solo adeuda su remanente) y
    // "Pagado" la suma de lo efectivamente pagado, abonos incluidos.
    const porPagar = unpaid
      .filter((n) => PENDING_STATES.includes(n.paymentStatus))
      .reduce((s, n) => s + amountDueOf(n), 0);
    const pagado = myNotes.reduce((s, n) => s + paidAmountOf(n), 0);
    return { porPagar, pagado };
  }, [unpaid, myNotes]);

  const goToNote = (id) => navigate(`/home/pagos/${id}`);

  // Una nota es pagable solo en estados de deuda (no en revisión / pagada / anulada).
  const payable = (n) => ['unpaid', 'partially_paid', 'overdue'].includes(n.paymentStatus);

  // Lleva a la vista de selección de método (no inicia el cargo directo).
  const handlePay = (note) => navigate(`/home/pagos/${note._id}/pagar`);

  // ── Tarjeta mobile reutilizable ──
  const MobileCard = ({ n, showPaid, showPay }) => (
    <div
      className="bill-card-item bill-card-item--clickable"
      role="button"
      tabIndex={0}
      onClick={() => goToNote(n._id)}
      onKeyDown={(e) => { if (e.key === 'Enter') goToNote(n._id); }}
    >
      <div className="bill-card-item__head">
        <span className="bill-card-item__title bill-td-track">#{n.folio ?? '—'}</span>
        <PaymentStatusBadge note={n} size="sm" />
      </div>
      <div className="bill-card-item__rows">
        <div className="bill-card-item__field bill-card-item__field--full">
          <span className="bill-card-item__k">{t("Periodo")}</span>
          <span className="bill-card-item__v bill-td-muted">
            {formatDate(n.periodStart)} → {formatDate(n.periodEnd)}
          </span>
        </div>
        <div className="bill-card-item__field">
          <span className="bill-card-item__k">{t("Total")}</span>
          <span className="bill-card-item__v bill-td-amount">{formatCurrency(n.totalAmount, n.currency)}</span>
        </div>
        <div className="bill-card-item__field">
          <span className="bill-card-item__k">{t("Pagado")}</span>
          <span className="bill-card-item__v bill-td-amount">{formatCurrency(paidAmountOf(n), n.currency)}</span>
        </div>
        <div className="bill-card-item__field">
          <span className="bill-card-item__k">{t("Saldo")}</span>
          <span className="bill-card-item__v bill-td-amount">{formatCurrency(amountDueOf(n), n.currency)}</span>
        </div>
        {dueDateStatus(n) && (
          <div className="bill-card-item__field">
            <span className="bill-card-item__k">{t("Vencimiento")}</span>
            <DueDateHint note={n} />
          </div>
        )}
        {showPaid && (
          <div className="bill-card-item__field">
            <span className="bill-card-item__k">{t("Pagada")}</span>
            <span className="bill-card-item__v bill-td-sub">{n.paidAt ? formatDate(n.paidAt) : '—'}</span>
          </div>
        )}
      </div>
      {showPay && (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="primary"
            fullWidth
            onClick={() => handlePay(n)}
          >
            {t("Pagar")}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="bill bill-page" style={{ padding: 16 }}>
      <PageHeader
        className="mb-6"
        icon={<Receipt className="w-6 h-6 text-fg-muted" />}
        title={t("Pagos")}
        subtitle={`${myNotes.length} ${myNotes.length === 1 ? t('nota de cobro') : t('notas de cobro')}`}
      />

      {loading ? (
        <div className="bill-card bill-card--clip">
          <TableSkeleton rows={6} columns={6} className="border-0" />
        </div>
      ) : (
        <>
          {/* Resumen */}
          {myNotes.length > 0 && (
            <div className="bill-strip" role="group" aria-label={t("Resumen")}>
              <div className="bill-strip__item">
                <span className="bill-strip__icon"><Receipt className="w-4 h-4" /></span>
                <span className="bill-strip__label">{t("Notas")}</span>
                <span className="bill-strip__value">{myNotes.length}</span>
              </div>
              <div className="bill-strip__item">
                <span className="bill-strip__icon bill-strip__icon--warning"><Clock className="w-4 h-4" /></span>
                <span className="bill-strip__label">{t("Por pagar")}</span>
                {/* Si falló la consulta de impagas no se sabe cuánto se debe: '—', no $0. */}
                <span className="bill-strip__value bill-td-amount">
                  {myDebtError ? '—' : formatCurrency(strip.porPagar)}
                </span>
              </div>
              <div className="bill-strip__item">
                <span className="bill-strip__icon" style={{ color: 'rgb(var(--status-success))' }}>
                  <CheckCircle2 className="w-4 h-4" />
                </span>
                <span className="bill-strip__label">{t("Pagado")}</span>
                <span className="bill-strip__value bill-td-amount">{formatCurrency(strip.pagado)}</span>
              </div>
            </div>
          )}

          {/* Notas por pagar */}
          <div className="bill-card bill-card--clip">
            <div className="bill-card__head">
              <h3 className="bill-card__title">
                {t("Notas por pagar")} <span>({myDebtError ? '—' : unpaid.length})</span>
              </h3>
            </div>
            {!myDebtError && myDebtCount > unpaid.length && (
              <div className="bill-note" style={{ margin: '0.75rem 1.25rem 0' }}>
                {t("Mostrando {{n}} de {{total}} notas por pagar.", { n: unpaid.length, total: myDebtCount })}
              </div>
            )}
            {/* Un error NO puede verse como "no tienes notas pendientes": sería falso. */}
            {myDebtError ? (
              <EmptyState
                icon={<AlertTriangle />}
                title={t("No se pudieron cargar tus notas por pagar.")}
                action={
                  <Button variant="outline" onClick={() => fetchMyDebtNotes()}>
                    {t("Reintentar")}
                  </Button>
                }
              />
            ) : unpaid.length === 0 ? (
              <EmptyState
                icon={<Wallet />}
                title={t("No tienes notas pendientes de pago")}
                description={t("Cuando tu operador emita una nota por cobrar, aparecerá acá para que la pagues.")}
              />
            ) : isMobile ? (
              <div className="bill-cards">
                {unpaid.map((n) => (
                  <MobileCard key={n._id} n={n} showPay={payable(n)} />
                ))}
              </div>
            ) : (
              <div className="bill-grid" role="table" style={{ '--bill-cols': UNPAID_COLS }}>
                <div className="bill-grid__head" role="row">
                  <span className="bill-grid__hcell" role="columnheader">{t("Folio")}</span>
                  <span className="bill-grid__hcell" role="columnheader">{t("Periodo")}</span>
                  <span className="bill-grid__hcell" role="columnheader">{t("Modo")}</span>
                  <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t("Total")}</span>
                  <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t("Pagado")}</span>
                  <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t("Saldo")}</span>
                  <span className="bill-grid__hcell" role="columnheader">{t("Estado")}</span>
                  <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t("Acciones")}</span>
                </div>
                {unpaid.map((n) => (
                  <div
                    key={n._id}
                    className="bill-grid__row bill-grid__row--clickable cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                    role="row"
                    tabIndex={0}
                    aria-label={`${t("Ver nota #")}${n.folio ?? '—'}`}
                    onClick={() => goToNote(n._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToNote(n._id);
                      }
                    }}
                  >
                    <span className="bill-td-track" role="cell">#{n.folio ?? '—'} <CopyButton value={n.folio} label={t('Copiar folio')} copiedMessage={t('Folio copiado')} /></span>
                    <span className="bill-td-muted" role="cell">
                      {formatDate(n.periodStart)}
                      <br />
                      <span className="bill-td-sub">→ {formatDate(n.periodEnd)}</span>
                    </span>
                    <span className="bill-td-muted" role="cell">
                      {CHARGE_MODE_LABELS[n.chargeMode] ?? n.chargeMode}
                    </span>
                    <span className="bill-grid__cell--end bill-td-amount" role="cell">
                      {formatCurrency(n.totalAmount, n.currency)}
                    </span>
                    <span className="bill-grid__cell--end bill-td-amount" role="cell">
                      {formatCurrency(paidAmountOf(n), n.currency)}
                    </span>
                    <span className="bill-grid__cell--end bill-td-amount" role="cell">
                      {formatCurrency(amountDueOf(n), n.currency)}
                    </span>
                    <span role="cell">
                      <PaymentStatusBadge note={n} />
                      <DueDateHint note={n} className="mt-1" />
                    </span>
                    <span className="bill-grid__cell--actions" role="cell" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => goToNote(n._id)}>{t("Ver")}</Button>
                      {payable(n) && (
                        <Button
                          size="sm"
                          variant="primary"
                          className="ms-2"
                          onClick={() => handlePay(n)}
                        >
                          {t("Pagar")}
                        </Button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Historial */}
          {history.length > 0 && (
            <div className="bill-card bill-card--clip" style={{ marginTop: '1rem' }}>
              <div className="bill-card__head">
                <h3 className="bill-card__title">{t("Historial")} <span>({history.length})</span></h3>
              </div>
              {isMobile ? (
                <div className="bill-cards">
                  {history.map((n) => (
                    <MobileCard key={n._id} n={n} showPaid />
                  ))}
                </div>
              ) : (
                <div className="bill-grid" role="table" style={{ '--bill-cols': HISTORY_COLS }}>
                  <div className="bill-grid__head" role="row">
                    <span className="bill-grid__hcell" role="columnheader">{t("Folio")}</span>
                    <span className="bill-grid__hcell" role="columnheader">{t("Periodo")}</span>
                    <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t("Total")}</span>
                    <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t("Pagado")}</span>
                    <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t("Saldo")}</span>
                    <span className="bill-grid__hcell" role="columnheader">{t("Estado")}</span>
                    <span className="bill-grid__hcell" role="columnheader">{t("Pagada")}</span>
                    <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t("Acciones")}</span>
                  </div>
                  {history.map((n) => (
                    <div
                      key={n._id}
                      className="bill-grid__row bill-grid__row--clickable cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                      role="row"
                      tabIndex={0}
                      aria-label={`${t("Ver nota #")}${n.folio ?? '—'}`}
                      onClick={() => goToNote(n._id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToNote(n._id);
                        }
                      }}
                    >
                      <span className="bill-td-track" role="cell">#{n.folio ?? '—'} <CopyButton value={n.folio} label={t('Copiar folio')} copiedMessage={t('Folio copiado')} /></span>
                      <span className="bill-td-muted" role="cell">
                        {formatDate(n.periodStart)}
                        <br />
                        <span className="bill-td-sub">→ {formatDate(n.periodEnd)}</span>
                      </span>
                      <span className="bill-grid__cell--end bill-td-amount" role="cell">
                        {formatCurrency(n.totalAmount, n.currency)}
                      </span>
                      <span className="bill-grid__cell--end bill-td-amount" role="cell">
                        {formatCurrency(paidAmountOf(n), n.currency)}
                      </span>
                      <span className="bill-grid__cell--end bill-td-amount" role="cell">
                        {formatCurrency(amountDueOf(n), n.currency)}
                      </span>
                      <span role="cell"><PaymentStatusBadge note={n} /></span>
                      <span className="bill-td-sub" role="cell">{n.paidAt ? formatDate(n.paidAt) : '—'}</span>
                      <span className="bill-grid__cell--actions" role="cell" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="outline" onClick={() => goToNote(n._id)}>{t("Ver")}</Button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PagosList;
